import { faExternalLink, faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Drawer } from '@sorare/core/src/atoms/layout/Drawer';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LinkOther } from '@sorare/core/src/atoms/navigation/Box';
import {
  BodyL,
  HeadlineL,
  LabelM,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import Dialog from '@sorare/core/src/components/dialog';
import { NBA_MARKET_PRIMARY } from '@sorare/core/src/constants/__generated__/routes';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { glossary } from '@sorare/core/src/lib/glossary';

import emirateCupLogo from './assets/emirateCupLogo.png';
import emirateRaffleModalBg from './assets/emirateRaffleModalBg.jpg';
import tickets from './assets/tickets.png';

const Wrapper = styled(Vertical)`
  align-items: center;
  justify-content: space-around;
  gap: var(--triple-unit);
  background: url(${emirateRaffleModalBg}) no-repeat;
  background-size: cover;
  background-position: center top;
  padding: var(--quadruple-unit) var(--double-unit);
`;

const Img = styled.img`
  object-fit: contain;
`;

const InfoWrapper = styled(Horizontal)`
  padding: var(--double-unit);
  border-radius: var(--double-unit);
  background-color: rgba(var(--c-rgb-neutral-1000), 0.05);
`;

const CTAsWrapper = styled(Vertical)`
  width: 100%;
  gap: var(--intermediate-unit);
`;

const EMIRATE_CUP_NIGHTS_T_AND_CS_URL =
  'https://sorare.com/emirates-cup-nights-t-and-cs';

type Props = {
  open: boolean;
  onClose: () => void;
};
export const EmirateVIPRaffleModal = ({ open, onClose }: Props) => {
  const isTabletAndAbove = useIsTabletAndAbove();

  const MainComponent = isTabletAndAbove ? Dialog : Drawer;
  const props = isTabletAndAbove
    ? {
        fullWidth: true,
        hideHeader: true,
        maxWidth: 'xs' as const,
      }
    : { side: 'bottom' as const, blur: true };

  return (
    <MainComponent open={open} onClose={onClose} {...props}>
      <Wrapper>
        <Img height={50} src={emirateCupLogo} alt="" />
        <Vertical gap={0.5} center>
          <HeadlineL>
            <FormattedMessage
              id="EmirateVIPRaffleModal.title"
              defaultMessage="Your Raffle Tickets"
            />
          </HeadlineL>
          <BodyL color="var(--c-neutral-600)">
            <FormattedMessage
              id="EmirateVIPRaffleModal.subtitle"
              defaultMessage="Emirates Cup Nights"
            />
          </BodyL>
          <LabelS color="var(--c-neutral-600)">
            <FormattedMessage
              id="EmirateVIPRaffleModal.endDate"
              defaultMessage="Ends November 15, 2024, 3pm (PT)"
            />
          </LabelS>
        </Vertical>
        <Img height={70} src={tickets} alt="" />
        <InfoWrapper>
          <FontAwesomeIcon icon={faInfoCircle} color="var(--c-neutral-600)" />
          <LabelM color="var(--c-neutral-600)">
            <FormattedMessage
              id="EmirateVIPRaffleModal.info"
              defaultMessage="Win more tickets by collecting Limited and Rare Cards during Emirates Cup Nights"
            />
          </LabelM>
        </InfoWrapper>
        <CTAsWrapper>
          <LinkOther
            as={Button}
            size="large"
            color="primary"
            to={NBA_MARKET_PRIMARY}
          >
            <FormattedMessage {...glossary.goToMarket} />
          </LinkOther>
          <LinkOther
            as={Button}
            size="large"
            target="_blank"
            color="transparent"
            to={EMIRATE_CUP_NIGHTS_T_AND_CS_URL}
          >
            <FontAwesomeIcon icon={faExternalLink} />
            <FormattedMessage
              id="EmirateVIPRaffleModal.TCs"
              defaultMessage="T&Cs"
            />
          </LinkOther>
        </CTAsWrapper>
      </Wrapper>
    </MainComponent>
  );
};
